/**
 * 同じ氏名(空白は無視)かメール(大小無視)の従業員が既に居れば知らせる。新規登録の入力欄の下に出す
 * (従業員の登録フォームと、新しい申請 › 入社申請の対象者段)。
 *
 * 二重登録に気づいてもらうための情報提供で、止めはしない(同姓同名の別人は普通に居る)。
 * 退職済み・匿名化済みは出さない。登録済みの人に入社申請を出す道は無くした(入社申請は
 * 従業員の新規入力からだけ出す)ので、「この人に申請だけ出す」の導線は置かない。
 */
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Space } from "antd";
import { useNavigate } from "react-router-dom";

import { useApi } from "../../lib/useApi";
import { useDebounced } from "../../lib/useDebounced";

export function SimilarWarning({
  familyName,
  givenName,
  email,
  onLeave,
}: {
  familyName: string;
  givenName: string;
  email: string;
  /** 進行中の申請へ飛ぶ前に呼ぶ(Modal を閉じるなど) */
  onLeave?: () => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const fn = useDebounced(familyName.trim(), 500);
  const gn = useDebounced(givenName.trim(), 500);
  const em = useDebounced(email.trim(), 500);
  const q = useQuery({
    queryKey: ["hr", "employees", "similar", fn, gn, em],
    queryFn: ({ signal }) => api.similarEmployees({ family_name: fn, given_name: gn, email: em }, signal),
    // 氏名は姓・名の両方が揃ってから。メールは単独でも引く
    enabled: (!!fn && !!gn) || !!em,
    staleTime: 10_000,
  });
  const rows = q.data ?? [];
  if (!rows.length) return null;
  return (
    <Alert
      type="warning"
      showIcon
      style={{ marginBottom: 24 }}
      message="同じ氏名/メールの従業員が居る(二重登録でないか確かめる)"
      description={
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {rows.map((r) => (
            <Space key={r.id} wrap size={8}>
              <span>
                {r.name}({r.emp_code}・{r.status_label})
                {r.match !== "name" && r.email ? ` ${r.email}` : ""}
              </span>
              {r.open_onboard_request_id != null && (
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    onLeave?.();
                    navigate(`/hr/wf-requests/${r.open_onboard_request_id}`);
                  }}
                >
                  入社申請 #{r.open_onboard_request_id} が進行中
                </Button>
              )}
            </Space>
          ))}
        </Space>
      }
    />
  );
}
